import React, { useEffect, useRef } from 'react';
import {
    select,
    selectAll,
    max,
    event,
    scaleLinear,
    scaleOrdinal,
    scaleBand,
    axisBottom,
    axisLeft
} from 'd3';
import { BarChartContainer } from './BarChart.style';

const DEFAULT_MARGIN = { top: 25, right: 20, bottom: 20, left: 55 };
const TRANSITION_TIME = 700;
const MARGIN_INLINE = 5;
const BAR_PADDING_RATIO = 0.25;
const ELLIPSIS = '...';

const BarChart = ({ dataset, allSelected, onBarClick, size, xLabel }) => {
    const svgRef = useRef();

    let svg;

    const margin = DEFAULT_MARGIN;

    const width = size.width - margin.left - margin.right;
    const height = size.height - margin.top - margin.bottom;

    const getXScale = () =>
        scaleLinear()
            .domain([0, dataset.length ? max(dataset, d => d.count) : 1])
            .range([0, width]);

    const getYScale = () =>
        scaleBand() // band scale for the y-axis, with one band for risk level
            .domain(dataset.map(d => d.text))
            .range([0, height])
            .padding(BAR_PADDING_RATIO);

    const getColors = () =>
        scaleOrdinal()
            .domain(dataset.map(d => d.severity))
            .range(dataset.map(d => d.color));

    const textOverflowEllipsis = text => {
        let tmpText = text;
        if (tmpText.length > 8) {
            tmpText = tmpText.slice(0, 7);
            tmpText += ELLIPSIS;
        }
        return tmpText;
    };

    const drawXAxis = xScale => {
        // from ui-components
        // limit the number of ticks if max is below TICKS_NUMBER to avoid having float numbers on the x-axis
        const TICKS_NUMBER = 6;
        const maxValue = max(dataset, d => d.count);
        let numberOfTicks = TICKS_NUMBER;
        if (!maxValue || maxValue === 0) {
            numberOfTicks = 1;
        } else if (maxValue > 0 && maxValue < TICKS_NUMBER) {
            numberOfTicks = maxValue;
        }

        const xAxis = axisBottom(xScale).ticks(numberOfTicks);

        select('.xAxis')
            .attr('transform', `translate(0, ${height})`)
            .transition()
            .duration(TRANSITION_TIME)
            .call(xAxis);
    };

    const drawYAxis = (yScale, allBarsSelected) => {
        const yAxis = axisLeft(yScale)
            .tickSize(0) // removing the ticks for the vertical axis
            .tickPadding(5)
            .tickFormat(label => textOverflowEllipsis(label));

        select('.yAxis')
            .transition()
            .duration(TRANSITION_TIME)
            .call(yAxis)
            .selectAll('text')
            .attr('class', text => {
                const curObj = dataset.length && dataset.filter(ele => ele.text === text)[0];
                return allBarsSelected
                    ? ''
                    : curObj && curObj.selected
                        ? 'selected'
                        : 'unselected_label';
            });
    };

    const drawBars = (xScale, yScale, allBarsSelected) => {
        // HORIZONTAL BARS
        const rects = svg
            .select('.bars')
            .selectAll('rect')
            .data(dataset);

        const colorScale = getColors();

        const onBarClickHandler = val => {
            onBarClick(val.text);

            const barElement = select(event.target);
            const { _groups } = barElement;
            const [firstGroup] = _groups;
            const [bar] = firstGroup;
            const classValue = bar.getAttribute('class');

            if (classValue === 'selected_bar') {
                select(event.target).attr('class', 'unselected_bar');
            } else {
                select(event.target).attr('class', 'selected_bar');
            }

            selectAll('rect[class=bar]').attr('class', 'unselected_bar');

            const unselectedCount = document.querySelectorAll('.unselected_bar').length;
            if (unselectedCount === dataset.length) {
                // unselected all, so revert to select all and replace the bar colour for each bar
                const elementList = document.querySelectorAll('rect');
                elementList.forEach(elem => elem.setAttribute('class', 'bar'));
            }
        };

        // append a rect element for each dataset point
        rects
            .join(
                enter => enter.append('rect'),
                update => update,
                exit =>
                    exit
                        .transition()
                        .attr('width', 0)
                        .attr('height', 0)
                        .remove()
            )
            .attr('x', 1) // each rectangle starts from the left and its respective band
            .attr('y', d => yScale(d.text))
            .attr('height', yScale.bandwidth())
            .attr('data-testid', d => `${d.text}_bar`)
            .on('click', val => onBarClickHandler(val))
            .transition()
            .duration(TRANSITION_TIME)
            .attr('width', d => xScale(d.count))
            .attr('fill', d => colorScale(d.severity))
            .attr(
                'class',
                d => (allBarsSelected ? 'bar' : d.selected ? 'selected_bar' : 'unselected_bar')
            );
    };

    const drawBarLabels = (xScale, yScale) => {
        const numberOnBar = select('.barLabels')
            .selectAll('text')
            .data(dataset);
        const dy = dataset.length > 0 ? svg.select('.yAxis .tick text').attr('dy') : 0;

        numberOnBar
            .join(enter => enter.append('text'), update => update, exit => exit.remove())
            .text(d => d.count)
            .attr('text-anchor', 'start')
            .attr('y', d => yScale(d.text))
            .attr('transform', () => `translate(0, ${yScale.bandwidth() / 2})`)
            .attr('dy', () => dy)
            .transition()
            .duration(TRANSITION_TIME)
            .attr('x', d => xScale(d.count) + MARGIN_INLINE)
            .attr('data-testid', d => `${d.text}_bar_value`);
    };

    const drawXLabel = () => {
        svg.select('.xAxisLabel').remove();

        if (dataset.length) {
            svg.append('text')
                .text(xLabel)
                .attr('class', 'xAxisLabel')
                .attr('x', width / 2)
                .attr('y', height + 45)
                .attr('text-anchor', 'middle');
        } else {
            svg.append('text')
                .attr('class', 'xAxisLabel')
                .attr('x', width / 2)
                .attr('y', height + 45)
                .text('No data to show')
                .transition()
                .duration(TRANSITION_TIME)
                .attr('y', height - 40)
                .attr('text-anchor', 'middle');
        }
    };

    useEffect(
        () => {
            // include the SVG and nested g element in which to plot the visualization
            svg = select(svgRef.current)
                .attr('viewBox', `0 0 ${size.width + margin.left} ${size.height + margin.top}`)
                .select('.container')
                .attr('transform', 'translate(85, 15)');

            // SCALES
            const xScale = getXScale();
            const yScale = getYScale();

            // Add x label
            drawXLabel();

            // AXES
            drawXAxis(xScale);
            drawYAxis(yScale, allSelected);

            // BARS
            drawBars(xScale, yScale, allSelected);

            // BAR LABELS
            drawBarLabels(xScale, yScale);
        },
        [allSelected, JSON.stringify(dataset)] // instead of using deep comaprison
    );
    return (
        <BarChartContainer>
            <svg ref={svgRef} width="99.9%" height="99.9%">
                <g className="container">
                    <text className="xAxisLabel" />
                    <g className="bars" />
                    <g className="barLabels" />
                    <g className="xAxis" />
                    <g className="yAxis" />
                </g>
            </svg>
        </BarChartContainer>
    );
};
export default BarChart;
